import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Menu, X } from 'lucide-react';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Button } from '@/client/components/ui/button';
import { cn } from '@/client/lib/utils';
import { isLocale, type Locale } from '@/client/i18n';
import { useMe } from '@/client/hooks/useMe';
import { api } from '@/client/lib/api';

const NAV_KEYS = [
  'about',
  'academy',
  'courses',
  'consultations',
  'mentoring',
  'contact',
] as const;

const NAV_PATHS: Record<(typeof NAV_KEYS)[number], string> = {
  about: 'o-dajane',
  academy: 'o-academy',
  courses: 'kurzy',
  consultations: 'konzultacie',
  mentoring: 'mentoring',
  contact: 'kontakt',
};

export function PublicHeader() {
  const { t } = useTranslation();
  const params = useParams();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';
  const prefix = `/${locale}`;
  const { data: me } = useMe();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Glass header becomes more opaque after a tiny scroll, so content
  // doesn't bleed through the nav as the user reads down the page.
  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 8);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on route change.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll while mobile drawer is open.
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  async function handleLogout() {
    await api('/api/v1/auth/session', { method: 'DELETE' });
    queryClient.setQueryData(['me'], null);
    navigate(`${prefix}/`);
  }

  return (
    <>
      <header
        className={cn(
          'sticky top-0 z-40 transition-all duration-300',
          'backdrop-blur-xl border-b',
          scrolled
            ? 'bg-white/85 border-glass-border-strong shadow-glass-soft'
            : 'bg-white/55 border-transparent',
        )}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-4">
          <Link
            to={prefix}
            aria-label="Akadémia Dajana Rodriguez"
            className="flex items-center group"
          >
            <img
              src="/brand/logo-text-purple.svg"
              alt=""
              className="h-8 sm:h-9 w-auto transition-transform group-hover:scale-[1.02]"
              loading="eager"
            />
          </Link>

          <nav
            aria-label="Primary"
            className="hidden xl:flex items-center gap-0.5 px-1.5 py-1.5 rounded-pill bg-white/60 backdrop-blur-md border border-glass-border-strong shadow-glass-soft"
          >
            {NAV_KEYS.map((key) => (
              <NavLink
                key={key}
                to={`${prefix}/${NAV_PATHS[key]}`}
                className={({ isActive }) =>
                  cn(
                    'whitespace-nowrap px-3 py-1.5 rounded-pill font-sans text-body-small font-medium transition-all',
                    isActive
                      ? 'bg-gradient-to-br from-primary to-primary-dark text-white shadow-[0_2px_8px_rgba(117,59,189,0.25)]'
                      : 'text-text-primary hover:text-primary hover:bg-primary-50',
                  )
                }
              >
                {t(`nav.${key}`)}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageSwitcher />
            {me ? (
              <>
                <Link to={`${prefix}/portal`} className="hidden sm:inline-flex">
                  <Button variant="ghost" size="sm">
                    {t('nav.portal')}
                  </Button>
                </Link>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleLogout}
                  className="hidden sm:inline-flex"
                >
                  {t('nav.logout')}
                </Button>
              </>
            ) : (
              <Link to={`${prefix}/prihlasenie`} className="hidden sm:inline-flex">
                <Button variant="primary" size="sm">
                  {t('nav.login')}
                </Button>
              </Link>
            )}

            <button
              type="button"
              className="xl:hidden inline-flex h-10 w-10 items-center justify-center rounded-pill bg-white/70 backdrop-blur-md border border-glass-border-strong text-primary hover:bg-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((value) => !value)}
            >
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer overlay */}
      <div
        className={cn(
          'fixed inset-0 z-30 xl:hidden transition-opacity duration-300',
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
        aria-hidden={!mobileOpen}
      >
        <button
          type="button"
          tabIndex={-1}
          aria-label="Close menu"
          className="absolute inset-0 bg-primary-900/40 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />

        <aside
          className={cn(
            'absolute right-0 top-0 h-full w-[88%] max-w-sm',
            'bg-white/95 backdrop-blur-2xl border-l border-glass-border-strong',
            'shadow-[0_-8px_60px_rgba(81,45,109,0.25)]',
            'transition-transform duration-300',
            mobileOpen ? 'translate-x-0' : 'translate-x-full',
          )}
        >
          <div className="px-6 pt-20 pb-8 h-full overflow-y-auto flex flex-col">
            <nav className="flex flex-col gap-1">
              {NAV_KEYS.map((key) => (
                <NavLink
                  key={key}
                  to={`${prefix}/${NAV_PATHS[key]}`}
                  className={({ isActive }) =>
                    cn(
                      'px-4 py-3 rounded-md font-sans text-body-large font-medium transition-colors',
                      isActive
                        ? 'bg-primary-50 text-primary'
                        : 'text-text-primary hover:bg-primary-50/60',
                    )
                  }
                >
                  {t(`nav.${key}`)}
                </NavLink>
              ))}
            </nav>

            <div className="mt-auto pt-8 flex flex-col gap-3">
              {me ? (
                <>
                  <Link to={`${prefix}/portal`}>
                    <Button variant="primary" size="lg" className="w-full">
                      {t('nav.portal')}
                    </Button>
                  </Link>
                  <Button
                    variant="ghost"
                    size="lg"
                    className="w-full"
                    onClick={handleLogout}
                  >
                    {t('nav.logout')}
                  </Button>
                </>
              ) : (
                <>
                  <Link to={`${prefix}/prihlasenie`}>
                    <Button variant="primary" size="lg" className="w-full">
                      {t('nav.login')}
                    </Button>
                  </Link>
                  <Link to={`${prefix}/registracia`}>
                    <Button variant="ghost" size="lg" className="w-full">
                      {t('auth.signup_title')}
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </aside>
      </div>
    </>
  );
}
